/**
 * Formatierer + Status-Vokabular (deutsch) fuer alle Screens.
 * Geld IMMER aus Integer-Cents (Konvention aus packages/db).
 */
import { KANAL_TITEL } from "@maestro/auswertung";
import type { EventStatus } from "./types";

// L11 (Antoine, 11.07.2026): Beträge IMMER mit zwei Nachkommastellen ("900,00 €"),
// auch bei glatten Euro-Werten — einheitliche Geld-Darstellung auf allen Screens.
const EUR_CENTS = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export function money(cents: number | null | undefined): string {
  if (cents == null) return "–";
  return EUR_CENTS.format(cents / 100);
}

/**
 * H7/N-F2: Parst einen als de-DE eingegebenen Dezimalwert (Geld/Prozent) zu einem Number.
 * Bewusst konservativ — lieber `null` (Validierungsfehler beim Aufrufer) als ein 1000-fach
 * falscher Betrag durch `parseFloat("1.500") === 1.5`:
 *  - Leerzeichen werden entfernt.
 *  - Komma = Dezimaltrennzeichen; Punkte gelten dann als Tausenderpunkte und fallen weg.
 *  - Ohne Komma werden Punkte als Tausendergruppierung entfernt, wenn jede Nachgruppe genau
 *    3 Ziffern hat („1.500" -> 1500, „1.234.567" -> 1234567); „1.5"/„1.50" bleibt Dezimalpunkt.
 *  - Alles Ungültige, Mehrdeutige (z. B. „1.2.3") oder Negative -> `null`.
 * @returns Number (>= 0) oder `null` bei leer/ungültig/mehrdeutig.
 */
export function parseGermanDecimal(input: string): number | null {
  const s = input.replace(/\s/g, "");
  if (s === "") return null;
  let normalized: string;
  if (s.includes(",")) {
    if ((s.match(/,/g) ?? []).length > 1) return null; // mehrere Kommas -> ungültig
    normalized = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(".")) {
    const parts = s.split(".");
    const grouped = parts.length >= 2 && /^\d{1,3}$/.test(parts[0]!) && parts.slice(1).every((g) => /^\d{3}$/.test(g));
    normalized = grouped ? parts.join("") : s;
  } else {
    normalized = s;
  }
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Wie {@link parseGermanDecimal}, aber direkt in verlustfrei gerundete Integer-Cents. */
export function parseGermanEurosToCents(input: string): number | null {
  const euros = parseGermanDecimal(input);
  return euros == null ? null : Math.round(euros * 100);
}

/**
 * L10 / W6 (BUGHUNT-2026-08-15): Cents als deutsche Euro-EINGABE ("1250" -> "12,50") — fuer die
 * Vorbefuellung von Preisfeldern. Vorher je eine lokale Kopie in builder.tsx und catalog.tsx,
 * waehrend pakete.tsx `toFixed(2)` mit Punkt zeigte. Rein kosmetisch: das Parsing
 * (parseGermanEurosToCents) akzeptiert Komma wie Punkt und bleibt unangetastet.
 */
export function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

/**
 * Anlass (`events.occasion`, Freitext) fuer die Anzeige: „firmenfeier“ → „Firmenfeier“ (Antoine
 * 16.10.2026). Nur der erste Buchstabe wird gross, der Rest bleibt; leer → `null`, damit
 * `anlassAnzeige(x) ?? "Vorgang"` greift. Der gespeicherte Wert bleibt unveraendert.
 */
export function anlassAnzeige(text: string | null | undefined): string | null {
  const t = text?.trim();
  return t ? t.charAt(0).toLocaleUpperCase("de-DE") + t.slice(1) : null;
}

/** `locale` optional (Default de-DE) — genutzt von der oeffentlichen Angebotsseite fuer EN-Events. */
export function formatDate(value: string | null | undefined, locale: string = "de-DE"): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** "Samstag, 12. Juli 2026" -- Wochentag + langes Datum fuer Detail-Kopfzeilen. */
export function formatWeekdayDate(value: string | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

/** "in 5 Tagen" / "heute" / "vor 2 Tagen" -- relative Tages-Angabe zu einem Datum (ohne Uhrzeit). */
export function daysUntil(value: string | null | undefined): string | null {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const t0 = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const d0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const days = Math.round((t0 - d0) / 86_400_000);
  if (days === 0) return "heute";
  if (days === 1) return "morgen";
  if (days === -1) return "gestern";
  if (days > 1) return `in ${days} Tagen`;
  return `vor ${Math.abs(days)} Tagen`;
}

/**
 * A6/E7 (Antoine, 13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md`): Läuft die Veranstaltung HEUTE?
 *
 * Grundlage der Gewichtungs-Regel für den Küchenzettel im Kopf der Vorgangsseite. Antoine
 * wörtlich: „Der Küchenzettel ist mir zu prominent. Wenn es zu einer Veranstaltung kommt, dann, am
 * Tag der Veranstaltung, wird dieser Küchenzettel benötigt." Der Zettel ist also kein Planungs-,
 * sondern ein Durchführungs-Werkzeug — an jedem anderen Tag konkurriert er nur mit den Aktionen,
 * die tatsächlich anstehen.
 *
 * Mehrtägige Veranstaltungen (`dateEnd`) zählen an JEDEM ihrer Tage als Veranstaltungstag — in der
 * Küche wird der Zettel dann auch an jedem dieser Tage gebraucht, nicht nur am ersten.
 *
 * Der Vergleich läuft bewusst über die lokale Zeitzone des Geräts (wie `daysUntil` darüber und wie
 * jede andere Datumsanzeige dieser Oberfläche): der Betreiber sitzt in seinem Betrieb, sein Gerät
 * steht auf dessen Zeitzone. Eine mandantenweite Zeitzone kennt MAESTRO bisher nicht — hier eine
 * zu erfinden wäre eine stille zweite Wahrheit gegenüber allen anderen Datumsangaben.
 */
export function istVeranstaltungstag(
  eventDate: string | null | undefined,
  dateEnd?: string | null,
  jetzt: Date = new Date(),
): boolean {
  if (!eventDate) return false;
  const start = new Date(eventDate);
  if (Number.isNaN(start.getTime())) return false;
  const ende = dateEnd ? new Date(dateEnd) : start;
  const letzterTag = Number.isNaN(ende.getTime()) ? start : ende;
  const tag = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const heute = tag(jetzt);
  return heute >= tag(start) && heute <= tag(letzterTag);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Relative Angabe ("Vor 2 Std.") fuer Listen; faellt auf Datum zurueck. */
export function timeAgo(value: string | null | undefined): string {
  if (!value) return "–";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "–";
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return "Gerade eben";
  if (mins < 60) return `Vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Gestern";
  if (days < 7) return `Vor ${days} Tagen`;
  return formatDate(value);
}

/* ----------------------------- Status-Vokabular ---------------------------- */

export type PillTone = "neutral" | "info" | "success" | "warn" | "danger" | "primary";

export interface StatusMeta {
  label: string;
  tone: PillTone;
  /** Ruhige Hinweise (Antoine, 13.10.2026): das Symbol traegt die Bedeutung, nicht die Farbe. */
  icon?: string;
  /** Tooltip der Pille (z. B. „68,00 € von 340,00 €“) — der Betrag steht nicht im Label. */
  title?: string;
  /** Mockup 19.10.2026: Bezahlt-Hinweise sind grün, auch wo `success` sonst ruhig-grau ist. */
  gruen?: boolean;
}

export const INQUIRY_STATUS: Record<string, StatusMeta> = {
  new: { label: "Neu", tone: "info", icon: "mail" },
  quoted: { label: "Angebot gesendet", tone: "warn", icon: "send" },
  won: { label: "Gewonnen", tone: "success", icon: "check" },
  lost: { label: "Abgesagt", tone: "neutral", icon: "block" },
  // Systemzustand waehrend der Erzeugung. Er fehlte hier - und weil StatusPill ohne Treffer `null`
  // zurueckgibt, zeigte die Liste bei einer haengengebliebenen Anfrage GAR NICHTS an (Antoines
  // Screenshot vom 30.07.2026: leere Status-Spalte, kein Knopf, kein Hinweis - die Anfrage sah
  // kaputt aus, ohne zu sagen warum). Ein sichtbarer Zustand ist immer besser als ein leerer.
  generating: { label: "Wird erzeugt", tone: "primary", icon: "progress_activity" },
};

/**
 * Kanal, über den eine Anfrage hereinkam — mit Material-Icon für die UI-Anzeige.
 *
 * P7: Die **Beschriftungen** kommen aus `@maestro/auswertung` (`KANAL_TITEL`), weil die
 * Auswertungsseite sie in ihre Sätze einbaut („Website-Formular: 8 von 22"). Zwei Listen mit
 * denselben Beschriftungen laufen auseinander — dann hieße derselbe Kanal in der Anfragenliste
 * anders als in der Auswertung. Das Icon bleibt hier: es ist reine Oberfläche und hat in einer
 * satzbildenden Regel nichts zu suchen.
 */
const SOURCE_ICON: Record<string, string> = {
  manual: "person",
  form: "public",
  chat: "chat",
  email: "mail",
  phone: "call",
  // Migration 341 (Posteingang-als-Anfrage): erster Schreibweg fuer source='whatsapp'.
  whatsapp: "chat",
};
export const INQUIRY_SOURCE: Record<string, { label: string; icon: string }> = Object.fromEntries(
  Object.entries(SOURCE_ICON).map(([key, icon]) => [key, { label: KANAL_TITEL[key] ?? key, icon }]),
);
export function inquirySourceMeta(source: string | null | undefined): { label: string; icon: string } {
  return (source && INQUIRY_SOURCE[source]) || { label: source || "Unbekannt", icon: "help" };
}

export const EVENT_STATUS: Record<EventStatus, StatusMeta> = {
  inquiry: { label: "Anfrage", tone: "info", icon: "mail" },
  offer_draft: { label: "Entwurf", tone: "neutral", icon: "edit_note" },
  offer_sent: { label: "Angebot gesendet", tone: "warn", icon: "send" },
  // Antoine, 14.09.2026: "Angebot gewählt" war unklar ("meinst du, dass der Kunde zugesagt hat?").
  // "Zugesagt" gewaehlt statt "Zusage"/"Buchung" - passt zum Partizip-Muster der Nachbar-Labels
  // (Bezahlt/Abgesagt/Durchgefuehrt) UND deckt sich mit der dritten Kanban-Spalte
  // (`AnfragenKanban.tsx`), die fuer dieselbe Statusgruppe bereits "Zugesagt" heisst.
  offer_chosen: { label: "Zugesagt", tone: "success", icon: "check" },
  // Ruhige Hinweise: "Abgelehnt"/"Abgesagt" sind Geschaeftsausgaenge, keine Fehler -> grau.
  offer_declined: { label: "Abgelehnt", tone: "neutral", icon: "block" },
  paid: { label: "Bezahlt", tone: "success", icon: "euro" },
  completed: { label: "Durchgeführt", tone: "info", icon: "task_alt" },
  // F1 (docs/KONZEPT-ABSAGE-STORNO-LEBENSZYKLUS.md, Antoine 10.09.2026): "Abgesagt" statt
  // "Storniert" - der Enum-Wert 'cancelled' und der gesamte bestehende Cancel-Workflow
  // bleiben technisch unveraendert, nur die Beschriftung aendert sich. Antoines
  // Begriffsregel: "Storno" ist bei ihm immer mit einer buchhalterischen Aufgabe
  // verbunden (Rechnung stornieren etc.) - das gilt weiterhin NUR bedingt, wenn
  // tatsaechlich Zahlungen vorliegen (s. cancellationTodos-Hinweis in event-detail.tsx,
  // der bewusst "Storniert" behaelt). Payment-/Invoice-Status (voided/canceled,
  // rechnungs-zustand.ts, payments.tsx, customer-detail.tsx, kundenportal.tsx) sind davon
  // unberuehrt und bleiben "Storniert".
  cancelled: { label: "Abgesagt", tone: "neutral", icon: "event_busy" },
  payment_failed: { label: "Zahlung fehlgeschlagen", tone: "danger", icon: "error" },
  // Punkt 93 (Antoine, 23.09.2026): nach VOLLER Rueckerstattung — nichts ist fehlgeschlagen.
  refunded: { label: "Erstattet", tone: "neutral", icon: "undo" },
  // Antoine 16.10.2026 (Migration 726): nach TEILerstattung — es steht nur ein Rest offen.
  // Grau wie jeder ruhige Hinweis; das Symbol traegt die Bedeutung.
  balance_due: { label: "Restforderung offen", tone: "neutral", icon: "hourglass_empty" },
  no_response: { label: "Keine Antwort", tone: "neutral", icon: "schedule" },
};

/**
 * Fachliche Sortier-Reihenfolgen je Domäne (Listenstandard, WARTESCHLANGE Punkt 95,
 * `docs/PLAN-listenstandard.md` § Status-Reihenfolgen, Brief § 3). Eine Statusspalte sortiert
 * nach dem Ablauf, nicht alphabetisch. Unbekannte Werte landen hinter allen bekannten, vor den
 * Leerwerten (`sortiereZeilen` in `hooks/useListSort.ts`).
 */
function reihenfolge(werte: readonly string[]): Readonly<Record<string, number>> {
  return Object.fromEntries(werte.map((w, i) => [w, i]));
}
/** Buchungen: Zugesagt < Zahlung fehlgeschlagen < Restforderung offen < Bezahlt < Durchgeführt < Erstattet < Abgesagt. */
export const BUCHUNG_STATUS_REIHENFOLGE = reihenfolge(["offer_chosen", "payment_failed", "balance_due", "paid", "completed", "refunded", "cancelled"]);
/** Anfragen: Wird erzeugt < Neu < Angebot gesendet < Gewonnen < Verloren. */
export const ANFRAGE_STATUS_REIHENFOLGE = reihenfolge(["generating", "new", "quoted", "won", "lost"]);
/** Zahlungen: Offen < In Bearbeitung < Fehlgeschlagen < Bezahlt < Erstattet < Storniert. */
export const ZAHLUNG_STATUS_REIHENFOLGE = reihenfolge(["pending", "processing", "failed", "succeeded", "refunded", "canceled"]);
/** Events: alle Werte in der Reihenfolge des Briefs (§ 3), „Erstattet“ (Punkt 93) vor „Abgesagt“. */
export const EVENT_STATUS_REIHENFOLGE = reihenfolge([
  "inquiry",
  "offer_draft",
  "offer_sent",
  "offer_chosen",
  "paid",
  "completed",
  "no_response",
  "offer_declined",
  "payment_failed",
  "balance_due",
  "refunded",
  "cancelled",
]);

/** N-11: sicherer Lookup — unbekannte/neue Event-Status crashen die UI nicht mehr (roher Code als
 *  Label, neutraler Ton als Fallback statt `EVENT_STATUS[status]` === undefined). */
export function eventStatusMeta(status: string): StatusMeta {
  return EVENT_STATUS[status as EventStatus] ?? { label: status, tone: "neutral" };
}

/**
 * Mandanten-Lifecycle-Status (Admin-Bereich V2, Migration 375 `tenants.status` —
 * `aktiv|gesperrt|archiviert`, docs/KONZEPT-ADMIN-BEREICH.md § "Lifecycle-UI": „StatusPill
 * (aktiv=success, gesperrt=warn, archiviert=neutral)"). Einzige Quelle fuer beide Aufrufer
 * (`tenants-liste.tsx` Spalte, `tenant-detail.tsx` Uebersicht-Kopf) — dieselbe Herleitung wie
 * `EVENT_STATUS`/`eventStatusMeta` oben, nicht zwei getrennte Ton-Zuordnungen.
 *
 * `tenantStatusMeta(undefined)` liefert bewusst `undefined` (nicht "neutral" mit leerem Label) —
 * `StatusPill` zeigt dann schlicht nichts, statt eine leere/falsche Pille zu rendern. Das deckt den
 * defensiven Fall ab, in dem `status` (aus welchem Grund auch immer) in der Antwort fehlt, ohne
 * dass die Seite abstuerzt (Muster `emailStatusMeta` oben). Ein unbekannter, aber VORHANDENER Wert
 * faellt wie bei `eventStatusMeta` auf einen neutralen Ton mit dem Rohwert als Label zurueck — die
 * DB-Seite hat dafuer zwar einen CHECK-Constraint (nur die drei genannten Werte moeglich), die UI
 * crasht trotzdem nicht blind, falls der Constraint je erweitert wird, bevor diese Zuordnung es ist.
 */
export const TENANT_STATUS: Record<string, StatusMeta> = {
  aktiv: { label: "Aktiv", tone: "success", icon: "check_circle" },
  gesperrt: { label: "Gesperrt", tone: "warn", icon: "lock" },
  archiviert: { label: "Archiviert", tone: "neutral", icon: "inventory_2" },
};
export function tenantStatusMeta(status: string | null | undefined): StatusMeta | undefined {
  if (!status) return undefined;
  return TENANT_STATUS[status] ?? { label: status, tone: "neutral" };
}

/**
 * Bughunt 27.08.2026 (Kalender): "Firma vor Personenname" wiederverwendbar statt an jeder Stelle
 * neu geschrieben (bisher informell z.B. `c.company || c.name` in customers.tsx). Leerer/nur aus
 * Leerzeichen bestehender Firmenname zaehlt als "nicht angegeben", nicht als Firma.
 */
export function firmaOderName(k: { name?: string | null; company?: string | null } | null | undefined): string {
  if (!k) return "—";
  return k.company?.trim() || k.name?.trim() || "—";
}

export const PAYMENT_STATUS: Record<string, StatusMeta> = {
  pending: { label: "Offen", tone: "warn", icon: "hourglass_empty" },
  processing: { label: "In Bearbeitung", tone: "info", icon: "progress_activity" },
  succeeded: { label: "Bezahlt", tone: "success", icon: "check" },
  failed: { label: "Fehlgeschlagen", tone: "danger", icon: "error" },
  refunded: { label: "Erstattet", tone: "neutral", icon: "undo" },
  canceled: { label: "Storniert", tone: "neutral", icon: "block" },
};

export const PAYMENT_TYPE_LABEL: Record<string, string> = {
  deposit: "Anzahlung",
  prepayment: "Vorauszahlung",
  final: "Restzahlung",
  full: "Gesamtbetrag",
};

/**
 * Rechnungsart -> Anzeigetext. Lag bis zum Dokumente-Reiter (16.09.2026) als `INV_TYPE_LABEL`
 * ausschliesslich in `pages/abrechnung.tsx`; seit die Dokumente-Ansicht dieselben Belege ein
 * zweites Mal auflistet (inklusive stornierter), gehoert die Uebersetzung neben
 * `PAYMENT_TYPE_LABEL` — eine Wahrheit statt zwei Abschriften.
 */
export const INVOICE_TYPE_LABEL: Record<"invoice" | "deposit" | "final" | "credit", string> = {
  invoice: "Rechnung",
  deposit: "Anzahlung",
  final: "Schlussrechnung",
  credit: "Gutschrift",
};

/**
 * Dateigroesse in der kuerzesten ehrlichen Form (B / KB / MB). Kam bis 16.09.2026 nur in
 * `pages/posteingang.tsx` vor; der Dokumente-Reiter der Abrechnung zeigt dieselben
 * Mail-Anhang-Metadaten und braucht exakt dieselbe Schreibweise.
 */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Material-Symbol fuer einen Anhang-MIME-Typ (geteilt: Posteingang + Abrechnung/Dokumente). */
export function attachmentIcon(mimeType: string): string {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "picture_as_pdf";
  return "description";
}

/**
 * Anhang-Typ als kurzer deutscher Text. Fuer die Chat-Anhaenge eines Vorgangs gibt es KEINEN
 * gespeicherten Dateinamen (`events.details.attachments` fuehrt nur id/key/contentType/uploadedAt,
 * s. `apps/api/src/lib/event-attachments.ts`) — statt einen Namen zu erfinden, benennt die
 * Oberflaeche den Typ.
 */
export function attachmentTypLabel(mimeType: string): string {
  if (mimeType === "application/pdf") return "PDF-Dokument";
  if (mimeType === "image/jpeg") return "Bild (JPEG)";
  if (mimeType === "image/png") return "Bild (PNG)";
  if (mimeType === "image/webp") return "Bild (WebP)";
  if (mimeType.startsWith("image/")) return "Bild";
  return "Datei";
}

/**
 * Kostenuebernahme-MFA-Hinweis (Migration 357, "alles, was von eSign zurueckkommt" — Antoine,
 * 03.09.2026): mappt die von eSignatures.io beim Signieren genutzten
 * `multi_factor_authentications`-Werte (lib/cost-acceptance.ts#buildCreateContractPayload) auf
 * einen kurzen Anzeigetext. Geteilt zwischen der Auftrags-Abrechnungsseite (abrechnung.tsx) und
 * dem Zahlungen-Detail-Drawer (payments.tsx) — eine Uebersetzung, nicht zwei.
 * Unbekannt/leer -> `null`: die UI zeigt dann NICHTS (kein Platzhalter-Text, kein Raten — das
 * Post-Signatur-Antwortformat war beim Bau nicht live gegen eSignatures.io verifiziert, s. PR-Doku).
 */
const MFA_METHODE_LABEL: Record<string, string> = {
  sms_verification_code: "Verifiziert per SMS",
  email_verification_code: "Verifiziert per E-Mail",
};
export function costAcceptanceMfaHinweis(methods: readonly string[] | null | undefined): string | null {
  if (!methods || methods.length === 0) return null;
  const labels = methods.map((m) => MFA_METHODE_LABEL[m]).filter((l): l is string => Boolean(l));
  return labels.length > 0 ? labels.join(" · ") : null;
}

export const ROLE_LABEL: Record<string, string> = {
  owner: "Inhaber",
  staff: "Mitarbeiter",
};

/** Versandstatus einer geloggten Mail (Migration 78) — geteilt zwischen dem Auftrags-Feed
 *  (event-detail.tsx) und dem Mail-Protokoll (mail-protokoll.tsx). */
export const EMAIL_STATUS_LABEL: Record<string, string> = {
  sent: "Gesendet",
  failed: "Fehlgeschlagen",
  delivered: "Zugestellt",
  bounced: "Unzustellbar",
  complained: "Beschwerde",
  // Punkt 111 (Migration 514): Brevo meldet soft_bounce/deferred — Brevo versucht es weiter.
  deferred: "Verzögert",
};

export function istMailFehlgeschlagen(status: string | null | undefined): boolean {
  return status === "failed" || status === "bounced" || status === "complained";
}

/**
 * Bughunt 28.08.2026 (Antoine, echter Vorgang ccab35ca-…): Erfolgs- und Fehlschlag-Pille beim
 * Mailversand nutzten beide denselben warmen Terracotta-/Rot-Ton (`primary` bzw. `error`
 * inline-gemischt) — auf den ersten Blick kaum zu unterscheiden, ein erfolgreicher Versand sah
 * aus wie ein Fehler. `EMAIL_STATUS_META` ordnet jedem Status eines von `PILL_CLASSES`
 * eindeutig unterscheidbaren Tonpaaren zu (`success` = gruen, `danger` = rot — visuell klar
 * getrennt, anders als vorher) PLUS ein Icon, das dieselbe Unterscheidung OHNE Farbe traegt
 * (Rot/Gruen-Sehschwaeche). Labels bleiben `EMAIL_STATUS_LABEL` als einzige Quelle.
 */
export interface EmailStatusMeta extends StatusMeta {
  icon: string;
}
const EMAIL_STATUS_TONE: Record<string, { tone: PillTone; icon: string }> = {
  sent: { tone: "success", icon: "check_circle" },
  delivered: { tone: "success", icon: "check_circle" },
  failed: { tone: "danger", icon: "error" },
  bounced: { tone: "danger", icon: "error" },
  complained: { tone: "danger", icon: "report" },
  deferred: { tone: "warn", icon: "schedule" },
};
export function emailStatusMeta(status: string | null | undefined): EmailStatusMeta | undefined {
  if (!status) return undefined;
  const ton = EMAIL_STATUS_TONE[status] ?? { tone: "neutral" as const, icon: "help" };
  return { label: EMAIL_STATUS_LABEL[status] ?? status, tone: ton.tone, icon: ton.icon };
}

/**
 * Bughunt 28.08.2026: Rohe Provider-Fehlertexte (Cloudflare Email Service + Resend-Fallback,
 * s. `lib/integrations.ts`) lesen sich wie ein Stack-Trace, z. B.
 * "Cloudflare Email Service: 10205: email.sending.error.email.recipient_suppressed" — fuer
 * Nicht-Entwickler unverstaendlich. Diese Funktion leitet daraus eine kurze deutsche Ursache ab.
 * Der Rohtext geht dabei NICHT verloren — er bleibt als aufklappbare "Technische Details"
 * erhalten (`MailFehlerHinweis` in components.tsx), diese Funktion liefert nur die Kurzfassung.
 *
 * Bewusst Teilstring-Erkennung statt strukturierter Fehlercodes: die Rohtexte kommen aus ZWEI
 * unabhaengigen Anbietern mit je eigenem Format, kein gemeinsames Schema. Ein unbekanntes
 * Muster crasht NICHT, sondern liefert einen generischen Fallback — der Rohtext bleibt ohnehin
 * einsehbar.
 */
export function mailFehlerKurzfassung(rawError: string | null | undefined): string {
  if (!rawError || !rawError.trim()) return "Der Versand ist fehlgeschlagen, ohne dass eine Ursache übermittelt wurde.";
  const text = rawError.toLowerCase();
  if (text.includes("recipient_suppressed")) {
    return "Der Empfänger hat frühere E-Mails abgelehnt oder als Spam markiert.";
  }
  if (text.includes("domain is not verified") || text.includes("validation_error")) {
    return "Die Absender-Domain ist beim Mail-Anbieter nicht verifiziert.";
  }
  return "Der Versand ist aus einem unbekannten Grund fehlgeschlagen.";
}

/**
 * G6 (26.08.2026, event-detail.tsx) verallgemeinert (28.08.2026) fuers Mail-Protokoll: buendelt
 * AUFEINANDERFOLGENDE Eintraege mit demselben Gruppenschluessel (typischerweise der Betreff
 * einer ausgehenden Mail — mehrere Zustellversuche/Retries) zu einer Gruppe, statt jeden
 * einzeln aufzulisten. `items` muss bereits neueste-zuerst sortiert sein (beide Aufrufer liefern
 * das schon so: event-detail.tsx `feed`, `/api/system/emails` `ORDER BY receivedAt DESC``) —
 * `primary` ist dadurch immer der JUENGSTE Versuch der Gruppe, worauf sich z. B. der
 * "Erneut senden"-Knopf in event-detail.tsx eindeutig bezieht. `null` als Schluessel startet
 * IMMER eine eigene Einzel-Gruppe (nie mit Nachbarn gebuendelt) — so bleiben z. B.
 * Status-Wechsel-Eintraege im Aktivitaets-Feed unangetastet.
 */
export interface VersuchsGruppe<T> {
  key: string;
  primary: T;
  extra: T[];
}
export function gruppiereWiederholteVersuche<T>(
  items: T[],
  getId: (item: T) => string,
  getGruppenSchluessel: (item: T) => string | null,
): VersuchsGruppe<T>[] {
  const gruppen: VersuchsGruppe<T>[] = [];
  for (const item of items) {
    const schluessel = getGruppenSchluessel(item);
    const letzte = gruppen[gruppen.length - 1];
    if (schluessel !== null && letzte && getGruppenSchluessel(letzte.primary) === schluessel) {
      letzte.extra.push(item);
    } else {
      gruppen.push({ key: getId(item), primary: item, extra: [] });
    }
  }
  return gruppen;
}

// Ruhige Hinweise (Antoine, 13.10.2026, Mockup "Weniger Farben bei Hinweisen", gilt fuer die
// gesamte Software): jeder Status ist GRAU, das Symbol traegt die Bedeutung. Rot bleibt allein
// echten Fehlern vorbehalten (`danger`), Orange allein Knoepfen. Die Ton-Namen bleiben als
// fachliche Einordnung erhalten (Sortierung, Tests, spaetere Auswertung) — nur die Farbe faellt weg.
// Ratsche: apps/web/test/ruhige-hinweise.test.ts.
const RUHIG = "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]";
export const PILL_CLASSES: Record<PillTone, string> = {
  neutral: RUHIG,
  info: RUHIG,
  success: RUHIG,
  warn: RUHIG,
  primary: RUHIG,
  danger: "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/[0.22] dark:text-[#e39b92]",
};

/**
 * UX-Paket 26.08.2026, Punkt 3: einzige Quelle für die Anzeige von `events.locationType`
 * (`'in_house' | 'off_site'`, DB-CHECK, kein eigener Enum-Typ). Bis dahin stand der Text an
 * sechs unabhängigen Stellen als Literal — teils sogar mit vertauschter Wortreihenfolge
 * ("Catering (außer Haus)" vs. "Außer Haus (Catering)"). `in_house` zeigt jetzt den
 * mandantenspezifischen Firmennamen statt eines pauschalen "Im Haus" — `companyName` kommt aus
 * `useCompanyName()` (`hooks/useCompanyName.ts`); ohne gepflegten Firmennamen bleibt der
 * bisherige neutrale Text "Im Haus (Event)" der Fallback, kein hartkodierter Betriebsname.
 */
export function locationTypeLabel(
  locationType: string | null | undefined,
  companyName: string | null,
  offSiteMode?: string | null,
): string | null {
  if (locationType === "in_house") return companyName ? `${companyName} (Event)` : "Im Haus (Event)";
  if (locationType === "off_site") {
    // Migration 361: Lieferung/Catering-Unterscheidung, optional — ungepflegt (offSiteMode
    // null/undefined) faellt auf den bisherigen, generischen Text zurueck (bewusst KEIN
    // "ungepflegt"-Hinweis hier, s. Migrationskopf: NULL heisst nicht gepflegt, nicht falsch).
    if (offSiteMode === "delivery") return "Außer Haus (Lieferung)";
    return "Außer Haus (Catering)";
  }
  return null;
}

/**
 * WARTESCHLANGE Punkt 50 (Teilentscheidung) — hat der KUNDE zugesagt oder der BETRIEB?
 *
 * Antoine, 19.09.2026: „Telefonisch angenommen ist wie von dir beschrieben, aber mit dem Vermerk,
 * dass der Betreiber geklickt hat."
 *
 * Der telefonische Abschluss setzt die Zustimmung mit Zeitstempel — als haette der Gast geklickt.
 * Im Vorgang stand danach nur „Verbindlich angenommen"; wer es war, liess sich allein an einer
 * technischen Angabe ablesen, die niemand ansieht (`chosenUserAgent`, gesetzt in
 * `routes/events.ts` beim telefonischen Abschluss). Das ist kein Nachweis, das sieht nur aus wie
 * einer — genau der Befund aus Punkt 50.
 *
 * Diese Funktion liest dieselbe Angabe aus und macht sie sichtbar. Sie erfindet nichts: steht dort
 * nichts vom Backoffice, war es der Gast, und es bleibt bei der bisherigen Darstellung.
 *
 * @returns den Namen der Person aus dem Betrieb, `""` wenn der Betrieb es war, der Name aber nicht
 *          mitgeschrieben wurde, und `null`, wenn der Gast selbst zugesagt hat.
 */
export function betriebsseitigeZusage(chosenUserAgent: string | null | undefined): string | null {
  if (!chosenUserAgent || !chosenUserAgent.startsWith("MAESTRO Backoffice")) return null;
  // Vom ERSTEN "(" bis zum LETZTEN ")" — ein Betriebsname darf selbst Klammern tragen
  // ("Ristorante (Storia) GmbH"), und dann ist der ganze Inhalt der Name, nicht ein Teil davon.
  const auf = chosenUserAgent.indexOf("(");
  const zu = chosenUserAgent.lastIndexOf(")");
  if (auf === -1 || zu <= auf + 1) return "";
  return chosenUserAgent.slice(auf + 1, zu).trim();
}

/** Punkt 114 (sql/517): Brevo-Ereignis je Mail, verdichtet vom Server (erstes/letztes/Anzahl). */
export interface MailEreignis {
  ereignis: string;
  erstes: string;
  letztes: string;
  anzahl: number;
}

/** Brevo-Ereignisnamen (Transaktions-Webhook) -> Anzeige. Unbekannte Namen erscheinen roh. */
export const MAIL_EREIGNIS_LABEL: Record<string, string> = {
  request: "gesendet",
  delivered: "zugestellt",
  deferred: "zurückgestellt",
  soft_bounce: "Soft Bounce",
  hard_bounce: "Hard Bounce",
  blocked: "blockiert",
  invalid_email: "ungültige Adresse",
  spam: "als Spam gemeldet",
  error: "Fehler",
  opened: "geöffnet",
  unique_opened: "erstmals geöffnet",
  proxy_open: "per Proxy geladen",
  unique_proxy_open: "erstmals per Proxy geladen",
  click: "geklickt",
  unsubscribed: "abgemeldet",
};

/** „zugestellt 14:02 · geöffnet 14:10 (3×) · geklickt 14:11“ — Datum nur, wenn es vom ersten Ereignis abweicht. */
export function formatMailEreignisse(ereignisse: MailEreignis[] | null | undefined): string {
  if (!ereignisse?.length) return "";
  const sortiert = [...ereignisse].sort((a, b) => a.erstes.localeCompare(b.erstes));
  const tag = (d: Date) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  const ersterTag = tag(new Date(sortiert[0]!.erstes));
  return sortiert
    .map((e) => {
      const d = new Date(e.erstes);
      const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
      const wann = tag(d) === ersterTag ? zeit : `${tag(d)} ${zeit}`;
      return `${MAIL_EREIGNIS_LABEL[e.ereignis] ?? e.ereignis} ${wann}${e.anzahl > 1 ? ` (${e.anzahl}×)` : ""}`;
    })
    .join(" · ");
}

/**
 * Markdown-Sternchen aus Katalog-Texten entfernen (Antoine, 12.10.2026: „Keine Sternchen am
 * Anfang"). Herkunft: der KI-Speisekarten-Import uebernimmt Fettdruck aus der Vorlage, z. B.
 * „**16 Bruschette für 6-8 Personen:** …". Die Daten bleiben unveraendert; bereinigt wird nur
 * Anzeige und Uebernahme ins Angebot. Paare (**fett**, *kursiv*) verlieren ihre Sternchen,
 * einzelne Sternchen am Anfang/Ende fallen weg.
 */
export function ohneSternchen(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*([^*\s][^*]*?)\*/g, "$1")
    .replace(/^\s*\*+\s*|\s*\*+\s*$/g, "")
    .trim();
}
